import Player from './Player'
import AudioTrackList from './AudioTrackList'
import AudioTrack from './AudioTrack'
import Emitter from './Emitter'

/* global __DEBUG__ */
const UIClasses = {
  controlbar: {
    'controls': 'jw-controlbar',
    'right': 'jw-controlbar-right-group',
    'left': 'jw-controlbar-left-group',
    'middle': 'jw-controlbar-center-group'
  },
  elements: {
    'play': 'jw-icon-playback',
    'timeline': ['jw-text-elapsed', 'jw-slider-time', 'jw-text-duration'],
    'elapsed': 'jw-text-elapsed',
    'duration': 'jw-text-duration',
    'timeslider': 'jw-slider-time',
    'rewind': 'jw-icon-rewind',
    'live': 'jw-text-alt',
    'next': 'jw-icon-next',
    'quality': 'jw-icon-hd',
    'captions': 'jw-icon-cc',
    'audiotracks': 'jw-icon-audio-tracks',
    'volume': 'jw-icon-volume',
    'cast': 'jw-icon-airplay',
    'chapters': 'jw-icon-chapters',
    'transcript': 'fc-icon-transcript',
    'fullscreen': 'jw-icon-fullscreen'
  }
}

class JW8Player extends Player {
  init() {
    this.playerType = 'JWPlayer'
    this.playerVersion = this.source.version
    this.container = this.source.getContainer()
    this.source._lock = 0

    this.audioTracks = new AudioTrackList()
    this.audioTracks.addTrack(new AudioTrack(true, 0, 'main'))
    this.audioTracks.addEventListener('change', e => {
      this.source.setCurrentAudioTrack(e.track.id)
    })

    super.init()

    if (__DEBUG__) {
      this.source.on('all', (type, e) => {
        console.debug(e, type)
      })
    }

    this.source.on('all', (type, e) => {
      this.element.dispatchEvent(Emitter.cloneEvent('Event', e, 'jw_' + type))
    }).on('audioTracks', e => {
      let currentAudioTrack = Math.max(0, this.source.getCurrentAudioTrack())
      e.tracks.forEach((track, id) => {
        this.audioTracks.addTrack(new AudioTrack(id === currentAudioTrack, id, (id === 0) ? 'main' : 'alternative', track.name, track.language))
      })
      this.audioTracks.length = e.tracks.length
    }).on('audioTrackChanged', e => {
      let track = this.audioTracks.getTrackById(e.currentTrack)
      if (track) {
        track.enabled = true
      }
    }).on('playlistItem', e => {
      this.updateMirroredElement()
      this.element.dispatchEvent(Emitter.createEvent('Event', 'loadedmetadata'));
    }).on('resize', e => {
      this.element.dispatchEvent(Emitter.createEvent('Event', 'resize'))
    }).on('fullscreen', e => {
      this.element.dispatchEvent(Emitter.createEvent('Event', 'fullscreenchange'))
    }).on('idle', e => {
      this.element.dispatchEvent(Emitter.createEvent('Event', 'ended'))
    }).on('error', e => {
      this.element._error = {
        code: 0,
        message: e.message
      }
      this.element.dispatchEvent(Emitter.createEvent('Event', 'error'))
    })
  }

  prepareElement () {
    let _this = this
    let _source = this.source

    function load (src) {
      if (typeof src === 'string') {
        src = { file: src }
      }
      _source.load(src)
    }


    function reorderUI (order) {
      let previousEl = undefined
      let controlbar = this.container.getElementsByClassName(UIClasses.controlbar.controls)[0]
      let group = UIClasses.controlbar.left

      order.forEach(function (name) {
        let className = UIClasses.elements[name]
        
        // Timeline is the only middle group
        if (name === 'timeline') {
          group = UIClasses.controlbar.middle
          previousEl = undefined
        }

        if (Array.isArray(className)) {
          className.forEach((e) => { reinsert(e) })
        } else {
          reinsert(className)
        }
        
        if (name === 'timeline') {
          group = UIClasses.controlbar.right
          previousEl = undefined
        }
      })

      function reinsert (className) {
        // Get controlbar group element
        if (typeof group === 'string') {
          group = controlbar.getElementsByClassName(group)
          if (!group) {
            console.error('Could not get controlbar sub element')
          } else {
            group = group[0]
          }
        }

        // Get the elements that correspond
        let elements = controlbar.getElementsByClassName(className)

        if (elements && elements.length != 0) {
          for (let element of elements) {
            // Insert elemnt into new position in controlbar
            if (previousEl) {
              group.insertBefore(element, previousEl.nextSibling)
            } else {
              group.prepend(element)
            }
            previousEl = element
          }
        }
      }
    }


    function insertIcon (icon, position) {
      let target = position.before ? position.before : position.after
      target = UIClasses.elements[target];
      let controlbar = this.container.getElementsByClassName(UIClasses.controlbar.controls)[0]
      let targetEl = controlbar.getElementsByClassName(target)[0]

      icon.classList.add('jw-icon', 'jw-icon-inline', 'jw-button-color', 'jw-reset', 'jw-off')

      if (position.before) {
        targetEl.parentNode.insertBefore(icon, targetEl)
      }
      else {
        targetEl.parentNode.insertBefore(icon, targetEl.nextSibling)
      }
    }
    function getOverlay () {
      let overlay = this.container.getElementsByClassName('fc-poster')
      if (overlay.length === 0) {
        return false
      } else {
        return overlay[0]
      }
    }

    function lock (id) {
      this._lock |= id

      this.container.classList.add('fc-locked')

      this.had_controls = this.controls
      this.controls = false
      _source.stop()

      this.trigger('playerlocked', id)
    }

    function unlock (id) {
      this._lock &= ~id

      this.controls = true
      if (!this._lock) {
        this.container.classList.remove('fc-locked')

        if (this.had_controls) {
          this.controls = true
        }
        this.trigger('playerunlocked')
        return true
      }
      return false
    }

    super.prepareElement()

    this.element.trigger = function (eventType, eventData = {}) {
      this.dispatchEvent(Emitter.createEvent('Event', eventType, eventData))
      _source.trigger(eventType, eventData)
    }

    this.element._error = null

    let events = {
      'canplay': null,
      'canplaythrough': null,
      'durationchange': null,
      'encrypted': null,
      'emptied': null,
      'error': null,
      'loadeddata': null,
      'loadedmetadata': null,
      'play': null,
      'playing': null,
      'seeked': null,
      'seeking': null,
      'stalled': null,
      'suspend': null,
      'timeupdate': null,
      'volumechange': null,
      'waiting': null,
      'waitingforkey': null,
      'playlistItem': null
    }

    let methods = {

      /* HTMLMediaElement methods */

      'addTextTrack': null,
      'canPlayType': null,
      'exitFullScreen': function () {
        _source.setFullscreen(false)
      },
      'fastSeek': null,
      'load': load,
      'pause': null,
      'play': function () {
        _source.play(true)
      },
      'requestFullScreen': function () {
        _source.setFullscreen(true)
      },
      'getContainer': function () {
        return _source.container
      },
      'insertIcon': insertIcon,
      'reorderUI': reorderUI,
      'lock': lock,
      'unlock': unlock,
    }

    let properties = {

      'id': {
        get: function () {
          return _source.id
        }
      },
      'parentElement': {
        get: function () {
          return _this.container
        }
      },
      'config': {
        get: function () {
          return _source.getConfig()
        }
      },
      'currentConfig': {
        get: function () {
          return _this.mergeObjects(_source.getConfig(), _source.getPlaylistItem())
        }
      },
      'overlay': {
        get: getOverlay
      },
      /* HTMLMediaElement properties */

      'audioTracks': {
        get: function () {
          return _this.audioTracks
        }
      },
      'autoplay': {
        get: function () {
          return !!_source.getConfig().autostart
        },
        set: function (value) {
          _source.setConfig({ autostart: !!value })
          return true
        }
      },
      'buffered': null,
      'controller': null,
      'controls': {
        get: function () {
          return _source.getControls()
        },
        set: function (value) {
          _source.setControls(!!value)
          return true
        }
      },
      'controlsList': null,
      'crossOrigin': null,
      'currentSrc': {
        get: function () {
          let playlistItem = _source.getPlaylistItem()
          if (playlistItem) {
            return playlistItem.file
          }
          return ''
        },
        set: load
      },
      'currentTime': {
        get: function () {
          return _source.getPosition()
        },
        set: function (value) {
          _source.seek(value)
        }
      },
      'defaultMuted': {
        get: function () {
          return !!_source.getConfig().mute
        },
        set: function (value) {
          _source.setConfig({ mute: !!value })
          return true
        }
      },
      'defaultPlaybackRate': null,
      'disableRemotePlayback': null,
      'duration': {
        get: function () {
          return _source.getDuration()
        }
      },
      'ended': null,
      'error': {
        get: function () {
          return _this.element._error
        }
      },
      'loop': {
        get: function () {
          return !!_source.getConfig().repeat
        },
        set: function (value) {
          _source.setConfig({ repeat: !!value })
          return true
        }
      },
      'mediaGroup': null,
      'muted': null,
      'networkState': null,
      'paused': null,
      'playbackRate': {
        get: function () {
          return _source.getPlaybackRate()
        },
        set: function (value) {
          _source.setPlaybackRate(value)
          return true
        }
      },
      'played': null,
      'preload': null,
      'readyState': null,
      'seekable': null,
      'seeking': null,
      'src': {
        get: function () {
          return _source.getConfig().playlist[0].file
        },
        set: load
      },
      'srcObject': null,
      'textTracks': null,
      'videoTracks': null,
      'volume': null,

      /* HTMLVideoElement properties */

      'height': {
        get: function () {
          return _source.getHeight()
        },
        set: function (value) {
          _source.resize(_source.getWidth(), value)
          return true
        }
      },
      'poster': {
        get: function () {
          let preview = _this.container.getElementsByClassName('jw-preview').item(0)
          if (preview) {
            let backgroundImage = preview.style.backgroundImage
            let matches = backgroundImage.match(/url\(["']?(.*?)["']?\)/)
            if (matches.length === 2) {
              return matches[1]
            }
          }
          return ''
        },
        set: function (value) {
          let preview = _this.container.getElementsByClassName('jw-preview').item(0)
          if (preview) {
            preview.style.setProperty('background-image', 'url("' + value + '")')
            return true
          }
          return false
        }
      },
      'videoHeight': null,
      'videoWidth': null,
      'width': {
        get: function () {
          return _source.getWidth()
        },
        set: function (value) {
          _source.resize(value, _source.getHeight())
          return true
        }
      }

    }

    this.updateMirroredElement()

    for (let key in events) {
      this.mapEvent(key, events[key])
    }

    for (let key in methods) {
      this.mapMethod(key, methods[key])
    }

    for (let key in properties) {
      this.mapProperty(key, properties[key])
    }

    _source.on('beforePlay buffer play', (e) => {
      // Prevent any playback when the player is locked
      if (this.element._lock) {
        _source.stop(true)
      }
    }).on('playlistItem', (e) => {
      setTimeout(() => {
        if (this.element._lock) {
          _source.controls = false
        }
      }, 0)
    })
  }

  updateMirroredElement () {
    let element = this.container.getElementsByTagName('video').item(0)
    if (!element) {
      console.log('Could not find element to mirror')
    }
    this.setMirroredElement(element)
  }

  static handles (source) {
    if (typeof source === 'object') {
      return (source.hasOwnProperty('version') && source.version.indexOf('jwplayer') >= 0)
    }
  }

}

export default JW8Player
